import React from "react";
import { Box, Typography } from "@mui/material";
import type { TicketCargoSummary } from "../../models/Ticket";
import { formatTicketCargoAddress, normalizeTicketCargoSummary } from "../../utils/ticket";
import { TruckLength, TruckType } from "../../utils/truck-utils";

export type BillingRecord = Record<string, unknown> & {
  id?: string | number;
  _id?: string | number;
};

export interface BillingTruckSummary {
  id: string;
  plate: string;
  type: string;
  length: string;
  plateColor: string;
}

const getNestedRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

export const getFirstDefined = (values: Array<unknown>): unknown => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

export const parseStringValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

export const parseNumberValue = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const extractBillingCargoIdFromTitle = (value: unknown): string => {
  const normalized = parseStringValue(value).trim();
  if (!normalized) return "";

  const objectIdMatch = normalized.match(/([a-fA-F0-9]{24})\s*$/);
  if (objectIdMatch?.[1]) {
    return objectIdMatch[1];
  }

  const segments = normalized
    .split("-")
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length >= 3) {
    return segments[segments.length - 1];
  }

  return "";
};

export const getBillingCargoId = (record: BillingRecord | null): string => {
  if (!record) return "";

  return parseStringValue(
    getFirstDefined([
      record.cargo_id,
      record.cargoId,
      record.subject,
      record.subject_id,
      record.subjectId,
      record.goods_id,
      record.goodsId,
      extractBillingCargoIdFromTitle(record.title),
      extractBillingCargoIdFromTitle(record.name),
      extractBillingCargoIdFromTitle(record.subject_name),
      extractBillingCargoIdFromTitle(record.subject_title),
    ]),
  );
};

export const normalizeBillingCargoSummary = (value: unknown): TicketCargoSummary | null => {
  return normalizeTicketCargoSummary(value);
};

export const mergeBillingCargoSummary = (
  base: TicketCargoSummary | null,
  resolved: TicketCargoSummary | null,
): TicketCargoSummary | null => {
  if (!base && !resolved) return null;
  if (!base) return resolved;
  if (!resolved) return base;

  return {
    id: resolved.id || base.id,
    name: resolved.name || base.name,
    image: resolved.image || base.image,
    from: resolved.from ?? base.from,
    to: resolved.to ?? base.to,
  };
};

const normalizeTruckType = (value: unknown): string => {
  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return "";
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? TruckType(parsed) : normalized;
  }

  const parsed = parseNumberValue(value);
  return parsed === null ? "" : TruckType(parsed);
};

const normalizeTruckLength = (value: unknown): string => {
  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return "";
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? TruckLength(parsed) : normalized;
  }

  const parsed = parseNumberValue(value);
  return parsed === null ? "" : TruckLength(parsed);
};

export const parseBillingCargo = (record: BillingRecord | null): TicketCargoSummary | null => {
  if (!record) return null;

  const waybillRecord = getNestedRecord(record.waybill);
  const orderRecord = getNestedRecord(record.order);
  const cargoCandidate = getFirstDefined([
    record.cargo,
    record.cargoInfo,
    record.subjectInfo,
    record.relatedCargo,
    record.goods,
    record.goodsInfo,
    waybillRecord?.cargo,
    orderRecord?.cargo,
  ]);

  const normalizedCandidate = normalizeTicketCargoSummary(cargoCandidate);
  if (normalizedCandidate) {
    return normalizedCandidate;
  }

  return normalizeTicketCargoSummary({
    id: getBillingCargoId(record),
    name: getFirstDefined([record.cargo_name, record.cargoName, record.goods_name, record.goodsName, record.subject_name]),
    image: getFirstDefined([record.cargo_image, record.cargoImage, record.goods_image, record.goodsImage]),
    from: getFirstDefined([
      record.from,
      record.origin,
      record.loadingAddress,
      record.loadAddress,
      record.pickupAddress,
      record.from_address,
      record.originAddress,
    ]),
    to: getFirstDefined([
      record.to,
      record.destination,
      record.unloadingAddress,
      record.unloadAddress,
      record.receiveAddress,
      record.to_address,
      record.destinationAddress,
    ]),
  });
};

export const parseBillingTruck = (record: BillingRecord | null): BillingTruckSummary | null => {
  if (!record) return null;

  const waybillRecord = getNestedRecord(record.waybill);
  const orderRecord = getNestedRecord(record.order);
  const truckRecord =
    getNestedRecord(
      getFirstDefined([
        record.truck,
        record.truckInfo,
        record.vehicle,
        record.vehicleInfo,
        record.car,
        record.carInfo,
        waybillRecord?.truck,
        orderRecord?.truck,
      ]),
    ) ?? {};

  const plate = parseStringValue(
    getFirstDefined([
      truckRecord.licensePlate,
      truckRecord.plate,
      truckRecord.plateNo,
      truckRecord.plate_no,
      truckRecord.carNo,
      truckRecord.car_no,
      record.truck_plate,
      record.truckPlate,
      record.vehicle_plate,
      record.vehiclePlate,
      record.plate,
    ]),
  );
  const type = normalizeTruckType(
    getFirstDefined([
      truckRecord.truckType,
      truckRecord.type,
      truckRecord.vehicleType,
      truckRecord.model,
      truckRecord.truck_type,
      record.truck_type,
      record.truckType,
      record.vehicle_type,
      record.vehicleType,
    ]),
  );
  const length = normalizeTruckLength(
    getFirstDefined([
      truckRecord.truckLength,
      truckRecord.length,
      truckRecord.vehicleLength,
      truckRecord.truck_length,
      record.truck_length,
      record.truckLength,
      record.vehicle_length,
      record.vehicleLength,
    ]),
  );
  const plateColor = parseStringValue(
    getFirstDefined([
      truckRecord.plateColor,
      truckRecord.plate_color,
      record.plate_color,
      record.plateColor,
    ]),
  );
  const id = parseStringValue(getFirstDefined([truckRecord.id, truckRecord._id, record.truck_id, record.truckId]));

  if (!plate && !type && !length && !plateColor && !id) {
    return null;
  }

  return {
    id,
    plate,
    type,
    length,
    plateColor,
  };
};

const InfoText: React.FC<{ value: string }> = ({ value }) => (
  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
    {value}
  </Typography>
);

export const BillingCargoCard: React.FC<{
  cargo: TicketCargoSummary;
  variant?: "compact" | "detail";
}> = ({ cargo, variant = "compact" }) => {
  const imageSize = variant === "detail" ? 96 : 56;
  const title = cargo.name || "关联货源";
  const fromLabel = formatTicketCargoAddress(cargo.from);
  const toLabel = formatTicketCargoAddress(cargo.to);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
        p: 1.25,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <Box
          sx={{
            width: imageSize,
            height: imageSize,
            flexShrink: 0,
            borderRadius: 1.25,
            overflow: "hidden",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {cargo.image ? (
            <Box
              component="img"
              src={cargo.image}
              alt={title}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              暂无图片
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            关联货源{cargo.id ? ` · ${cargo.id}` : ""}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.25,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: variant === "detail" ? 2 : 1,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-all",
            }}
          >
            {title}
          </Typography>
          <InfoText value={`发货地：${fromLabel}`} />
          <InfoText value={`收货地：${toLabel}`} />
        </Box>
      </Box>
    </Box>
  );
};

export const BillingTruckCard: React.FC<{
  truck: BillingTruckSummary;
}> = ({ truck }) => {
  const lines = [
    truck.plate ? `车牌号：${truck.plate}` : "",
    truck.type ? `车型：${truck.type}` : "",
    truck.length ? `车长：${truck.length}` : "",
    truck.plateColor ? `车牌颜色：${truck.plateColor}` : "",
    truck.id ? `车辆ID：${truck.id}` : "",
  ].filter(Boolean);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
        p: 1.25,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        关联车辆
      </Typography>
      <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {lines.map((line) => (
          <InfoText key={line} value={line} />
        ))}
      </Box>
    </Box>
  );
};
